import React, { useEffect } from "react";

// Redux
import { useSelector } from "react-redux";

// React Query
import { useMutation, useQueryClient, useIsFetching } from "@tanstack/react-query";

// MUI
import { Typography, Box, Skeleton } from "@mui/material";

// Third party
import { SnackbarKey, useSnackbar } from "notistack";

// API
import { sendConfirmationEmailCognitoUsers } from "@DataPages/api/users";

// Components
import EmptyBody from "@Layouts/components/Basics/EmptyBody";
import UserPageTable from "@DataPages/components/Users/Tables/UserPageTable";
import TableLoader from "@Layouts/components/Skeletons/TableLoader";
import RoundedBox from "@Layouts/components/Basics/RoundedBox";
import ShowInformation from "@Layouts/components/Basics/ShowInformation";
import { SnackbarsActions, SnackbarsContent } from "@Layouts/components/Snackbars";

// Assets
import empty from "@/assets/images/commons/empty.png";

// Definitions
import { IRowData } from "@DataPages/types/users/users.d";
import { RootState } from "@/redux/store";

// Constants
import { DATE_FORMAT } from "@/Constants/date";

type TUserBodyProps = {
	selectionLocationId: string | undefined;
	selectionType: string | undefined;
	position: string | undefined;
	isLoadingMutations: boolean;
	setIsLoadingMutations: (loading: boolean) => void;
	resetSelects: () => void;
};
const UsersBody: React.FC<TUserBodyProps> = (props: TUserBodyProps) => {
	const location = useSelector((state: RootState) => state.location.location);
	const { enqueueSnackbar } = useSnackbar();
	const queryClient = useQueryClient();
	const positionTableData = queryClient.getQueryData<IRowData[]>([
		"positionTable",
		props.selectionLocationId,
		props.selectionType,
		props.position,
	]);
	const updatedAt = queryClient.getQueryData<Date | null>(["usersPageLastUpdate", location?.locationId]);
	const isFetchingPositionTableData = useIsFetching({ queryKey: ["positionTable"] }) > 0;
	const loadingUpdate = useIsFetching({ queryKey: ["usersPageLastUpdate"] }) > 0;

	const mutation = useMutation({
		mutationFn: (usernames: string[]) => sendConfirmationEmailCognitoUsers(location?.locationId ?? "", usernames),
		onMutate: () => {
			props.setIsLoadingMutations(true);
		},
		onError: (error: unknown) => {
			const message = (error as Error)?.toString().replace(/Error:/gi, " ");
			const renderSnackbarsActions = (key: SnackbarKey) => {
				return <SnackbarsActions key={key.toString()} index={key} />;
			};
			enqueueSnackbar(<SnackbarsContent message={message} critical={true} />, {
				variant: "error",
				preventDuplicate: true,
				action: renderSnackbarsActions,
			});
			props.setIsLoadingMutations(false);
		},
		onSuccess: () => {
			props.setIsLoadingMutations(false);
		},
	});

	const handleSendConfirmationEmailCognitoUsers = async (emails: string[]) => {
		mutation.mutate(emails);
	};

	useEffect(() => {
		if (positionTableData?.length === 0) {
			props.resetSelects();
		}
	}, [props, positionTableData]);

	return (
		<>
			<RoundedBox sx={{ height: "unset !important" }} className="min-h-[628px]">
				{/* if no position selected */}
				{!props.position || positionTableData?.length === 0 ? (
					<EmptyBody image={empty} title={"No Position selected"} bodyHeight={"475px"} />
				) : (
					// if position is selected
					<Box className="min-h-[520px] ">
						<Typography className={"page-title"}>{props.position}</Typography>
						{isFetchingPositionTableData ? (
							// if position selected but loading data
							<>
								<Box mb={2} className="flex justify-end items-center space-x-2">
									<Skeleton variant="circular" width={30} height={30} />
									<Skeleton variant="circular" width={30} height={30} />
									<Skeleton variant="circular" width={30} height={30} />
								</Box>
								<TableLoader content rows={3} multiTitles={6} />
							</>
						) : (
							// If position selected and there's data
							<UserPageTable
								data={positionTableData}
								handleSendConfirmationEmailCognitoUsers={handleSendConfirmationEmailCognitoUsers}
								isLoadingMutations={props.isLoadingMutations}
								selectionType={props.selectionType}
								setIsLoadingMutations={props.setIsLoadingMutations}
							/>
						)}
					</Box>
				)}
			</RoundedBox>
			<Box className={`${loadingUpdate ? "pt-3" : "p-2"}`}>
				<ShowInformation
					alternativeMessage={"Last Updated at"}
					useAlternative={true}
					alternativeDate={updatedAt}
					show={true}
					format={DATE_FORMAT.usDateTime12hUnpadded}
					loading={loadingUpdate}
					error={false}
				/>
			</Box>
		</>
	);
};

export default UsersBody;
